import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Plus, Edit, Archive, UserCheck, UserX, Shield, AlertCircle, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import PhoneInput from './ui/PhoneInput';
import EmailInput from './ui/EmailInput';

export default function GestionAdmins() {
  const { getAuthHeader } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('tous');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    role: 'admin',
    telephone: '',
    departement: '',
    fonction: ''
  });

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/admins`,
        { headers: getAuthHeader() }
      );
      setAdmins(response.data);
    } catch {
      toast.error('Erreur lors du chargement des administrateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async () => {
    try {
      const _response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/admins`,
        formData,
        { headers: getAuthHeader() }
      );
      
      toast.success('Administrateur créé avec succès ! Email de bienvenue envoyé.');
      setShowModal(false);
      resetForm();
      loadAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateAdmin = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/admins/${selectedAdmin.id}`,
        formData,
        { headers: getAuthHeader() }
      );
      
      toast.success('Administrateur mis à jour avec succès');
      setShowModal(false);
      resetForm();
      loadAdmins();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSuspendAdmin = async (adminId, suspend) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/admins/${adminId}/suspend`,
        { suspended: suspend },
        { headers: getAuthHeader() }
      );
      
      toast.success(suspend ? 'Administrateur suspendu' : 'Administrateur réactivé');
      loadAdmins();
    } catch {
      toast.error('Erreur lors de la suspension');
    }
  };

  const handleArchiveAdmin = async (adminId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir archiver cet administrateur ?')) return;
    
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/admins/${adminId}/archive`,
        {},
        { headers: getAuthHeader() }
      );
      
      toast.success('Administrateur archivé');
      loadAdmins();
    } catch {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const _handleReassignAdmin = async (oldAdminId, newAdminId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/admins/${oldAdminId}/reassign`,
        { new_admin_id: newAdminId },
        { headers: getAuthHeader() }
      );
      
      toast.success('Données réattribuées avec succès');
      loadAdmins();
    } catch {
      toast.error('Erreur lors de la réattribution');
    }
  };

  const openCreateModal = () => {
    setModalMode('create');
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (admin) => {
    setModalMode('edit');
    setSelectedAdmin(admin);
    setFormData({
      nom: admin.nom || '',
      prenom: admin.prenom || '',
      email: admin.email || '',
      role: admin.role || 'admin',
      telephone: admin.telephone || '',
      departement: admin.departement || '',
      fonction: admin.fonction || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      prenom: '',
      email: '',
      role: 'admin',
      telephone: '',
      departement: '',
      fonction: ''
    });
    setSelectedAdmin(null);
  };

  const filteredAdmins = admins.filter(admin => {
    const matchSearch = 
      admin.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchStatut = 
      filterStatut === 'tous' ||
      (filterStatut === 'actifs' && admin.is_active && !admin.suspended) ||
      (filterStatut === 'suspendus' && admin.suspended) ||
      (filterStatut === 'archives' && admin.archived);
    
    return matchSearch && matchStatut;
  });

  const getStatutBadge = (admin) => {
    if (admin.archived) {
      return <Badge className="bg-gray-600 text-white font-semibold">Archivé</Badge>;
    }
    if (admin.suspended) {
      return <Badge className="bg-orange-600 text-white font-semibold">Suspendu</Badge>;
    }
    if (admin.is_active) {
      return <Badge className="bg-green-600 text-white font-semibold">Actif</Badge>;
    }
    return <Badge className="bg-slate-600 text-white font-semibold">Inactif</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return <div className="flex justify-center p-4 md:p-8 lg:p-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Shield className="h-7 w-7" />
            Gestion des Administrateurs
          </CardTitle>
          <CardDescription className="text-white/90">
            Créez et gérez les comptes administrateurs de la plateforme
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Barre d'actions */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="flex gap-2 flex-1 w-full md:w-auto">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={filterStatut}
            onChange={(e) => setFilterStatut(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="tous">Tous</option>
            <option value="actifs">Actifs</option>
            <option value="suspendus">Suspendus</option>
            <option value="archives">Archivés</option>
          </select>
        </div>

        <Button onClick={openCreateModal} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" />
          Nouvel Administrateur
        </Button>
      </div>

      {/* Liste des admins */}
      <div className="grid gap-4">
        {filteredAdmins.map(admin => (
          <Card key={admin.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Info principale */}
                <div className="flex-1 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {admin.prenom} {admin.nom}
                      </h3>
                      <p className="text-sm text-gray-600">{admin.email}</p>
                      {admin.fonction && (
                        <p className="text-sm text-gray-500 mt-1">{admin.fonction}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {getStatutBadge(admin)}
                      <Badge className="bg-purple-600 text-white capitalize font-semibold">
                        {admin.role}
                      </Badge>
                    </div>
                  </div>

                  {/* Dates importantes */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <p className="text-gray-500">Création</p>
                      <p className="font-medium">{formatDate(admin.created_at)}</p>
                    </div>
                    {admin.date_activation && (
                      <div>
                        <p className="text-gray-500">Activation</p>
                        <p className="font-medium text-green-600">{formatDate(admin.date_activation)}</p>
                      </div>
                    )}
                    {admin.date_suspension && (
                      <div>
                        <p className="text-gray-500">Suspension</p>
                        <p className="font-medium text-orange-600">{formatDate(admin.date_suspension)}</p>
                      </div>
                    )}
                    {admin.date_resiliation && (
                      <div>
                        <p className="text-gray-500">Résiliation</p>
                        <p className="font-medium text-red-600">{formatDate(admin.date_resiliation)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex lg:flex-col gap-2 justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditModal(admin)}
                    className="flex-1 lg:flex-none"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Modifier
                  </Button>

                  {!admin.archived && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSuspendAdmin(admin.id, !admin.suspended)}
                      className={`flex-1 lg:flex-none ${admin.suspended ? 'text-green-600' : 'text-orange-600'}`}
                    >
                      {admin.suspended ? (
                        <>
                          <UserCheck className="h-4 w-4 mr-2" />
                          Réactiver
                        </>
                      ) : (
                        <>
                          <UserX className="h-4 w-4 mr-2" />
                          Suspendre
                        </>
                      )}
                    </Button>
                  )}

                  {!admin.archived && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleArchiveAdmin(admin.id)}
                      className="flex-1 lg:flex-none text-gray-600"
                    >
                      <Archive className="h-4 w-4 mr-2" />
                      Archiver
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredAdmins.length === 0 && (
          <Card className="p-12 text-center">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucun administrateur trouvé</p>
          </Card>
        )}
      </div>

      {/* Modal Création/Édition */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>
                {modalMode === 'create' ? 'Nouvel Administrateur' : 'Modifier l\'Administrateur'}
              </CardTitle>
              <CardDescription>
                {modalMode === 'create' 
                  ? 'Un email de bienvenue sera envoyé automatiquement avec les identifiants'
                  : 'Mettez à jour les informations de l\'administrateur'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Prénom *</label>
                  <input
                    type="text"
                    value={formData.prenom}
                    onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Nom *</label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <EmailInput
                    value={formData.email}
                    onChange={(val) => setFormData({...formData, email: val})}
                    placeholder="email@exemple.fr"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Téléphone</label>
                  <PhoneInput
                    value={formData.telephone}
                    onChange={(val) => setFormData({...formData, telephone: val})}
                    placeholder="Numéro de téléphone"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Rôle *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="admin">Administrateur</option>
                    <option value="super_admin">Super Administrateur</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Fonction</label>
                  <input
                    type="text"
                    value={formData.fonction}
                    onChange={(e) => setFormData({...formData, fonction: e.target.value})}
                    placeholder="Ex: Responsable Commercial"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Département</label>
                  <input
                    type="text"
                    value={formData.departement}
                    onChange={(e) => setFormData({...formData, departement: e.target.value})}
                    placeholder="Ex: Direction Commerciale"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setShowModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  onClick={modalMode === 'create' ? handleCreateAdmin : handleUpdateAdmin}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {modalMode === 'create' ? 'Créer et Envoyer Email' : 'Mettre à Jour'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
